import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import SegmentList from './SegmentList';
import ClipboardTool from '../ClipboardTool/ClipboardTool';
import ContextHelp from '../../ContextHelp/ContextHelp';
interface MeasurementDisplayProps {
  measurements: { distanceKm: number, distanceNm: number, areaKm2: number, areaM2: number, areaHa: number };
  isCollapsed: boolean;
  toggleCollapse: () => void;
  trashLastSegment: () => void;
  trashAllSegments: () => void;
  segmentLabels: GeoJSON.Feature<GeoJSON.Point>[];
}

const MeasurementDisplay: React.FC<MeasurementDisplayProps> = ({
  measurements,
  isCollapsed,
  toggleCollapse,
  trashLastSegment,
  trashAllSegments,
  segmentLabels
}) => {
  // Local state for the whole-panel minimize. Default open on first show; on
  // mobile (~< 768px) the panel otherwise blocks most of the map and you
  // can't keep adding points without dismissing it.
  const [isPanelMinimized, setIsPanelMinimized] = useState(false);

  // Compact summary shown when the panel is minimized so the user can still
  // see the running totals without expanding back.
  const compactArea = measurements.areaKm2 < 1
    ? `${measurements.areaM2.toFixed(0)} m²`
    : `${measurements.areaHa.toFixed(2)} Ha`;
  const showArea = measurements.areaM2 > 0 || measurements.areaKm2 > 0;

  return (
    <div className={`measurements ${isPanelMinimized ? 'is-minimized' : ''}`}>
      <h3>
        <ContextHelp title="Help with Measuring Tool" context="measure-tool-help" />
        <span>Measurements</span>
        <button
          type="button"
          className="measurements-minimize-btn"
          onClick={() => setIsPanelMinimized(prev => !prev)}
          aria-expanded={!isPanelMinimized}
          aria-label={isPanelMinimized ? 'Expand measurements panel' : 'Minimize measurements panel'}
          title={isPanelMinimized ? 'Expand' : 'Minimize'}
        >
          <FontAwesomeIcon icon={isPanelMinimized ? faChevronDown : faChevronUp} />
        </button>
      </h3>

      {isPanelMinimized && (
        <div className="measurements-summary">
          <span>{measurements.distanceKm.toFixed(2)} km</span>
          {showArea && <span className="measurements-summary-sep">·</span>}
          {showArea && <span>{compactArea}</span>}
        </div>
      )}

      {!isPanelMinimized && (<>
      <p className="measurements-label">Distance</p>
      <ul>
        <li>
          <span className="dist-km">{measurements.distanceKm.toFixed(2)}</span>
          <span className="unit">km</span>
          <ClipboardTool textToCopy={measurements.distanceKm.toFixed(2) + ' km'} />
        </li>
        <li>
          <span className="dist-nm">{measurements.distanceNm.toFixed(2)}</span>
          <span className="unit">nm</span>
          <ClipboardTool textToCopy={measurements.distanceNm.toFixed(2) + ' nm'} />
        </li>
      </ul>

      <p className="measurements-label">Area</p>
      <ul>
        {measurements.areaKm2 < 1 ? (
          <li>
            <span className="area-m2">{measurements.areaM2.toFixed(1)}</span>
            <span className="unit">m²</span>
            <ClipboardTool textToCopy={measurements.areaM2.toFixed(1) + ' m²'} />
          </li>
        ) : (
          <li>
            <span className="area-km2">{measurements.areaKm2.toFixed(2)}</span>
            <span className="unit">km²</span>
            <ClipboardTool textToCopy={measurements.areaKm2.toFixed(2) + ' km²'} />
          </li>
        )}
        <li>
          <span className="area-ha">{measurements.areaHa.toFixed(2)}</span>
          <span className="unit">Ha</span>
          <ClipboardTool textToCopy={measurements.areaHa.toFixed(2) + ' Ha'} />
        </li>
      </ul>

      <p className="measurements-label measurements-segments-header">
        <span>Segments</span>
        <button
          type="button"
          className="measurements-collapse-btn"
          onClick={toggleCollapse}
          aria-expanded={!isCollapsed}
          aria-controls="collapsible-content"
          aria-label={isCollapsed ? 'Expand segments' : 'Collapse segments'}
        >
          <FontAwesomeIcon icon={isCollapsed ? faChevronDown : faChevronUp} />
        </button>
      </p>
      <div
        id="collapsible-content"
        style={{
          height: isCollapsed ? '0px' : 'auto',
          overflow: 'hidden',
          transition: 'height 0.3s ease',
        }}
      >
        <SegmentList
          segmentLabels={segmentLabels}
          trashLastSegment={trashLastSegment}
          trashAllSegments={trashAllSegments}
        />
      </div>
      </>)}
    </div>
  );
};

export default MeasurementDisplay;